import type { TFunction } from 'i18next';
import { DateTime } from 'luxon';
import { RRule, type Options } from 'rrule';
import type { Schedule } from '~/generated/graphql';
import { formatTime } from '~/lib/datetime';

export const getEndDate = (options: Partial<Options>) => {
  if (options.until) return options.until;
  if (options.count) {
    const occurrences = new RRule(options).all();
    return occurrences.at(occurrences.length - 1);
  }
};

export const isExpired = (rrule: string | null | undefined) => {
  const options = RRule.parseString(rrule ?? 'FREQ=DAILY');
  const endDate = getEndDate(options);

  if (endDate) {
    const until = DateTime.fromJSDate(endDate, { zone: 'UTC' })
      .plus({ days: 1 })
      .setZone('local', { keepLocalTime: true });
    return DateTime.now() >= until;
  }

  return false;
};

// The RRule library provides a decent toText implementation but needed to be tweaked to better fit our needs.
export const getScheduleDescription = (schedule: Schedule, t: TFunction) => {
  const options = RRule.parseString(schedule.rrule ?? 'FREQ=DAILY');

  const dayAbbreviations: Record<string, string> = {
    Sunday: t('common.schedule_description.day_abbreviations.sunday'),
    Monday: t('common.schedule_description.day_abbreviations.monday'),
    Tuesday: t('common.schedule_description.day_abbreviations.tuesday'),
    Wednesday: t('common.schedule_description.day_abbreviations.wednesday'),
    Thursday: t('common.schedule_description.day_abbreviations.thursday'),
    Friday: t('common.schedule_description.day_abbreviations.friday'),
    Saturday: t('common.schedule_description.day_abbreviations.saturday'),
  };

  let desc = new RRule(options).toText(undefined, undefined, (year, month, day) =>
    DateTime.fromFormat(`${month} ${day}, ${year}`, 'MMMM d, yyyy').toFormat('LLL d, yyyy'),
  );

  // Replace 'every day' with translation
  desc = desc.replace(/every day/gi, t('common.schedule_description.every_day'));

  // If it's a 1 time thing, strip out silly stuff
  if (schedule.count === 1) desc = desc.replace(/every day for/i, '');

  if (schedule.allDay) {
    desc = t('common.schedule_description.all_day_prefix') + desc;
  } else {
    // This provides a better description for items scheduled for one day.
    if (
      options.dtstart &&
      // starts and ends on same day
      (options.dtstart.toISOString() === options.until?.toISOString() ||
        // starts on some day with only 1 execution
        (options.freq === RRule.DAILY && options.count === 1))
    ) {
      const date = DateTime.fromJSDate(options.dtstart, { zone: 'UTC' }).toFormat('MMM dd, yyyy');
      desc = t('common.schedule_description.on_prefix') + date;
    }

    if (schedule.startTime && schedule.endTime) {
      const start = formatTime(schedule.startTime);
      const end = formatTime(schedule.endTime);
      desc +=
        t('common.schedule_description.from') + start + t('common.schedule_description.to') + end;
    }
  }

  // Swap out long day names with short
  desc = desc.replace(
    /\b(?:Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\b/g,
    (match) => dayAbbreviations[match] || match,
  );

  // Swap out English weekday initials with localized abbreviations
  const initialsMap: Record<string, string> = {
    Mon: t('common.schedule_description.day_abbreviations.monday'),
    Tue: t('common.schedule_description.day_abbreviations.tuesday'),
    Wed: t('common.schedule_description.day_abbreviations.wednesday'),
    Thu: t('common.schedule_description.day_abbreviations.thursday'),
    Fri: t('common.schedule_description.day_abbreviations.friday'),
    Sat: t('common.schedule_description.day_abbreviations.saturday'),
    Sun: t('common.schedule_description.day_abbreviations.sunday'),
  };
  desc = desc.replace(/\b(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\b/g, (match) => initialsMap[match] || match);

  // Capitalize first letter
  return desc.charAt(0).toUpperCase() + desc.slice(1);
};
